import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  adminJourneyOverride,
  adminJourneyRecompute,
  fetchAdminJourneyLogs,
  fetchJourneyDebug,
} from "@/lib/sacred-journey/sacred-journey-api";
import { Loader2, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";

const ARCHETYPES = [
  "seeker", "lover", "warrior", "healer", "builder", "mystic", "sage", "protector", "leader", "creator", "witness", "rebel", "survivor",
];
const GATES = [
  "fear", "anger", "attachment", "pride", "confusion", "comfort", "chaos", "aggression", "escapism",
];
const ADHIKARA = ["sraddha", "mumukshutva", "sadhana_shakti", "nishtha"];
const PATHWAYS = ["bhakti", "karma", "jnana", "raja"];

export function JourneyEngineAdmin() {
  const [email, setEmail] = useState("");
  const [journey, setJourney] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [overrides, setOverrides] = useState({
    archetype_id: "",
    karmic_gate_id: "",
    adhikara_level_id: "",
    pathway_id: "",
  });

  const loadLogs = async () => {
    try {
      setLogs(await fetchAdminJourneyLogs());
    } catch {
      setLogs([]);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const loadUser = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setMessage("");
    try {
      const debug = await fetchJourneyDebug(email.trim());
      if (debug.error) {
        setJourney(null);
        setMessage("User not found");
      } else {
        setJourney(debug.raw_journey);
        setMessage(`Loaded ${debug.email || debug.user_id}`);
      }
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Load failed");
    }
    setLoading(false);
  };

  const recompute = async () => {
    setLoading(true);
    try {
      const j = await adminJourneyRecompute(email.trim());
      setJourney(j);
      setMessage("Recomputed");
      loadLogs();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Recompute failed");
    }
    setLoading(false);
  };

  const applyOverride = async () => {
    const clean = Object.fromEntries(
      Object.entries(overrides).filter(([, v]) => v && v !== "_none")
    );
    if (!Object.keys(clean).length) {
      setMessage("Pick at least one override");
      return;
    }
    setLoading(true);
    try {
      const j = await adminJourneyOverride(email.trim(), clean);
      setJourney(j);
      setMessage("Override applied & recomputed");
      loadLogs();
    } catch (e) {
      setMessage(e?.response?.data?.detail || "Override failed");
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6" data-testid="journey-engine-admin">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-lg">Journey Engine</CardTitle>
          <Button size="sm" variant="outline" asChild>
            <Link to="/admin/journey-test">Open Journey QA</Link>
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>User email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@example.com" />
            </div>
            <Button type="button" onClick={loadUser} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Load
            </Button>
            <Button type="button" variant="secondary" onClick={recompute} disabled={!email.trim() || loading}>
              Recompute
            </Button>
          </div>
          {message && <p className="text-sm text-muted-foreground">{message}</p>}
        </CardContent>
      </Card>

      {journey && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Current mapping</CardTitle>
          </CardHeader>
          <CardContent className="text-xs space-y-1 font-mono">
            <p>Archetype: {journey.archetype?.name} ({journey.archetype?.id})</p>
            <p>Gate: {journey.karmic_gate?.name} · {journey.secondary_gate?.name}</p>
            <p>Adhikāra: {journey.adhikara_level?.name}</p>
            <p>Pathway: {journey.pathway?.name}</p>
            <p className="text-muted-foreground">Last: {journey.last_computed}</p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Override & recompute</CardTitle>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <OverrideSelect
            label="Archetype"
            value={overrides.archetype_id}
            options={ARCHETYPES}
            onChange={(v) => setOverrides((o) => ({ ...o, archetype_id: v }))}
          />
          <OverrideSelect
            label="Karmic gate"
            value={overrides.karmic_gate_id}
            options={GATES}
            onChange={(v) => setOverrides((o) => ({ ...o, karmic_gate_id: v }))}
          />
          <OverrideSelect
            label="Adhikāra"
            value={overrides.adhikara_level_id}
            options={ADHIKARA}
            onChange={(v) => setOverrides((o) => ({ ...o, adhikara_level_id: v }))}
          />
          <OverrideSelect
            label="Pathway"
            value={overrides.pathway_id}
            options={PATHWAYS}
            onChange={(v) => setOverrides((o) => ({ ...o, pathway_id: v }))}
          />
          <Button type="button" className="sm:col-span-2" onClick={applyOverride} disabled={!email.trim() || loading}>
            Apply overrides
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Admin logs</CardTitle>
        </CardHeader>
        <CardContent className="max-h-48 overflow-y-auto text-xs space-y-2">
          {logs.length === 0 && <p className="text-muted-foreground">No logs yet.</p>}
          {logs.map((log) => (
            <p key={log.id}>
              {log.at} · {log.action} · {log.user_id}
              {log.detail && ` · ${log.detail}`}
            </p>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function OverrideSelect({ label, value, options, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || "_none"} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="_none">—</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>
              {o}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
